import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ArrowUpRight, MapPin } from "@phosphor-icons/react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "residential", label: "Residential" },
  { key: "interior", label: "Interior" },
  { key: "exterior", label: "Exterior" },
  { key: "commercial", label: "Commercial" },
];

export default function Gallery() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const filtered = filter === "all" ? projects : projects.filter((p) => p.category === filter);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-20 md:py-32 px-5 md:px-10 lg:px-16"
    >
      <a id="work" className="absolute -top-20" aria-hidden="true" />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <p className="section-num mb-3">03 — Recent work</p>
          <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tighter">
            Selected<br />projects.
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`chip ${filter === f.key ? "active" : ""}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 md:gap-6">
        {filtered.map((p, i) => (
          <button
            key={p.id}
            data-testid={`project-${p.id}`}
            onClick={() => setActive(p)}
            className={`group relative border-2 border-[#111] overflow-hidden text-left transition-transform hover:-translate-y-1 ${
              i % 5 === 0 ? "col-span-12 md:col-span-7 aspect-[16/10]" :
              i % 5 === 1 ? "col-span-12 md:col-span-5 aspect-[4/5]" :
              i % 5 === 2 ? "col-span-12 md:col-span-4 aspect-[4/5]" :
              i % 5 === 3 ? "col-span-12 md:col-span-4 aspect-[4/5]" :
              "col-span-12 md:col-span-4 aspect-[4/5]"
            }`}
          >
            <img
              src={p.image_url}
              alt={`${p.title} — painting project in ${p.suburb || 'Melbourne'} by The Melbourne Painters`}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              decoding="async"
              width="800"
              height="1000"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
            <div className="absolute inset-0 p-5 md:p-7 flex flex-col justify-between">
              <div className="flex justify-between items-start">
                <span className="text-[10px] uppercase tracking-widest bg-[#F2F2EF] text-[#111] px-2 py-1 border-2 border-[#111]">
                  {p.category}
                </span>
                <span className="bg-[#D85C3A] border-2 border-[#111] p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowUpRight size={20} weight="bold" className="text-white" />
                </span>
              </div>
              <div className="text-white">
                <h3 className="font-display text-2xl md:text-3xl uppercase leading-tight tracking-tight">
                  {p.title}
                </h3>
                {p.suburb && (
                  <div className="flex items-center gap-1 text-xs uppercase tracking-widest mt-2 opacity-80">
                    <MapPin size={14} weight="duotone" /> {p.suburb}
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {active && (
        <div
          data-testid="project-modal"
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 slide-up"
          onClick={() => setActive(null)}
        >
          <div
            className="bg-[#F2F2EF] border-2 border-[#111] max-w-3xl w-full max-h-[90vh] overflow-auto shadow-brut"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-[16/10] overflow-hidden border-b-2 border-[#111]">
              <img src={active.image_url} alt={active.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-6 md:p-8">
              <span className="chip mb-3">{active.category}</span>
              <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight mb-2 mt-2">{active.title}</h3>
              {active.suburb && <div className="text-sm text-[#666] mb-3">{active.suburb}, Melbourne</div>}
              <p className="text-base leading-relaxed">{active.description}</p>
              <button
                data-testid="close-project-modal"
                onClick={() => setActive(null)}
                className="btn-outline-brut mt-6"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
